/* eslint-disable no-param-reassign */
import { getParsedValue } from "./form.service";

const associatedIdLabel = "associatedIdLabel";
const associatedIdentifier = "associatedIdentifier";

export const getRelationalDgKey = () => "relationalDgLink";

export const getDgOptionValue = (data = {}) => data[getRelationalDgKey()];

export function getDgOptionLabel(identifierLabel, rec) {
  let label = "";
  if (!identifierLabel) return null;
  identifierLabel.forEach((item) => {
    const labels = item.indexOf(",") !== -1 ? item.split(",") : item;
    if (Array.isArray(labels) && labels.length > 0) {
      labels.forEach((labelItem, i) => {
        if (rec[labelItem]) {
          label = i === 0 ? rec[labelItem] : `${label} ${rec[labelItem]}`;
        }
      });
    } else if (rec[labels]) {
      label = rec[labels];
    }
  });
  return label;
}

const isFalsy = (value) => [null, undefined, ""].includes(value);

export function getDgIdentifier(config, data, isDropdown) {
  let uniqueId = null;
  if (config.conditional && isDropdown) {
    const { when, eq } = config.conditional;
    if (
      isFalsy(when) ||
      isFalsy(eq) ||
      !eq.includes(data[when]) ||
      data.status !== "saved"
    )
      return uniqueId;
  }
  const identifierParts = [];
  config.uniqueIdentifier.forEach((id) => {
    if (data[id]) identifierParts.push(data[id]);
  });
  if (identifierParts.length > 1) uniqueId = identifierParts.join("-");
  else if (identifierParts.length === 1) [uniqueId] = identifierParts;
  return uniqueId;
}

export const getDgOptionList = (sourceDg, config) => {
  const optionsList = sourceDg
    .filter((rec) => getDgIdentifier(config, rec, true))
    .map((rec) => ({
      label: getDgOptionLabel(config.identifierLabel, rec),
      value: getDgOptionValue(rec),
      identifier: getDgIdentifier(config, rec),
    }));
  return optionsList;
};

export const isAssociateField = ({
  sourceDatagrid,
  uniqueIdentifier,
  identifierLabel,
} = {}) => Boolean(sourceDatagrid && uniqueIdentifier && identifierLabel);

// relational dg
export const isSourceDg = ({ associatedDatagrids, identifiers } = {}) =>
  Boolean(associatedDatagrids && identifiers);

export const addCustomPropsToRecord = (setValue, el) => {
  const { label, identifier } = el?.props?.item || {};
  setValue(associatedIdLabel, label);
  setValue(associatedIdentifier, identifier);
};

export const setDgLinkKey = (properties, record = {}) => {
  const relationalDgKey = getRelationalDgKey();
  if (!record[relationalDgKey] && properties) {
    const { identifiers = "" } = properties;
    if (identifiers && typeof identifiers === "string") {
      const identifierKeys = identifiers.split(",").map((key) => key.trim());
      const uniqueKeyParts = [];
      // Collect values from all identifier keys
      identifierKeys.forEach((key) => {
        if (record[key]) uniqueKeyParts.push(record[key]);
      });
      // Only create relational key if we have at least one identifier value
      if (uniqueKeyParts.length > 0) {
        // eslint-disable-next-line no-param-reassign
        record[relationalDgKey] = window.btoa(new Date().getTime());
      }
    }
  }
};

export const getDgLinkKeys = (getDgState, associatedDgs = "") => {
  const { dgConfigs } = getDgState() || {};
  return associatedDgs.split(",").reduce((acc, cur) => {
    const { dgLinkKey = null } = dgConfigs[cur]?.custom
      ? getParsedValue(dgConfigs[cur]?.custom, "getDgLinkKeysMap")
      : {};
    return { ...acc, [cur]: dgLinkKey };
  }, {});
};

const isRecordAssociated = (sourceRecValue, assocRecValue) => {
  if (Array.isArray(assocRecValue))
    return assocRecValue.includes(sourceRecValue);
  return sourceRecValue && sourceRecValue === assocRecValue;
};

const findOrCreateDgEntry = (associatedDg, dgKey) => {
  let entry = associatedDg.find((item) => item.dgKey === dgKey);
  if (!entry) {
    entry = { dgKey, records: [] };
    associatedDg.push(entry);
  }
  return entry;
};

const findMatchingRecordsInDg = (
  dgKey,
  sourceRecord,
  getValues,
  dgLinkKeyMap
) => {
  const relationalDgKey = getRelationalDgKey();
  const associatedDg = getValues(dgKey);

  if (!associatedDg || !associatedDg.length) {
    return { matchingRecords: [] };
  }

  const matchingRecords = [];
  const sourceRecValue = sourceRecord[relationalDgKey];

  associatedDg.forEach((assocRec, recordIndex) => {
    const assocRecValue = assocRec[dgLinkKeyMap[dgKey]];
    if (isRecordAssociated(sourceRecValue, assocRecValue)) {
      const hasMultiAssociation =
        Array.isArray(assocRecValue) &&
        assocRecValue.length > 1 &&
        assocRecValue.includes(sourceRecValue);

      const recordInfo = {
        index: recordIndex,
        record: assocRec,
        hasMultiAssociation,
      };

      matchingRecords.push(recordInfo);
    }
  });
  return { matchingRecords };
};

const mergeChainedAssociations = (mainResult, chainedResult) => {
  if (!chainedResult.hasAssociation) return;
  mainResult.hasAssociation = true;

  chainedResult.associatedDg.forEach((chainedEntry) => {
    const existingEntry = mainResult.associatedDg.find(
      (item) => item.dgKey === chainedEntry.dgKey
    );

    if (existingEntry) {
      const existingIndexes = new Set(
        existingEntry.records.map((r) => r.index)
      );
      chainedEntry.records.forEach((record) => {
        if (!existingIndexes.has(record.index)) {
          existingEntry.records.push(record);
        }
      });
    } else {
      const newEntry = {
        dgKey: chainedEntry.dgKey,
        records: [...chainedEntry.records],
      };
      mainResult.associatedDg.push(newEntry);
    }
  });
};

const processChainedAssociations = (
  dgKey,
  recordsToProcess,
  getValues,
  getDgState,
  processedDatagrids
) => {
  const { dgConfigs } = getDgState() || {};
  const associatedDgProps = dgConfigs[dgKey]?.properties;

  if (!associatedDgProps?.associatedDatagrids || !recordsToProcess.length) {
    return { hasAssociation: false, associatedDg: [] };
  }

  const combinedResult = { hasAssociation: false, associatedDg: [] };

  recordsToProcess.forEach((recordInfo) => {
    // Only process chained associations for records that will be completely removed
    if (!recordInfo.hasMultiAssociation) {
      // eslint-disable-next-line no-use-before-define
      const chainedResult = getAssociatedDgInfo(
        associatedDgProps,
        getValues,
        getDgState,
        recordInfo.record,
        new Set(processedDatagrids)
      );
      if (chainedResult.hasAssociation) {
        mergeChainedAssociations(combinedResult, chainedResult);
      }
    }
  });

  return combinedResult;
};

const processSingleDatagrid = (
  dgKey,
  sourceRecord,
  getValues,
  getDgState,
  dgLinkKeyMap,
  processedDatagrids
) => {
  if (processedDatagrids.has(dgKey)) {
    return { hasAssociation: false, associatedDg: [] };
  }
  processedDatagrids.add(dgKey);

  const { matchingRecords } = findMatchingRecordsInDg(
    dgKey,
    sourceRecord,
    getValues,
    dgLinkKeyMap
  );

  const result = { hasAssociation: false, associatedDg: [] };

  if (matchingRecords.length > 0) {
    result.hasAssociation = true;
    const dgEntry = findOrCreateDgEntry(result.associatedDg, dgKey);

    dgEntry.records = matchingRecords.map(({ index, hasMultiAssociation }) => ({
      index,
      hasMultiAssociation,
    }));

    // Process chained associations for all matching records
    // The function will internally filter for records with hasMultiAssociation: false
    const chainedResult = processChainedAssociations(
      dgKey,
      matchingRecords,
      getValues,
      getDgState,
      processedDatagrids
    );
    mergeChainedAssociations(result, chainedResult);
  }
  return result;
};

export function getAssociatedDgInfo(
  properties,
  getValues,
  getDgState,
  record = {},
  processedDatagrids = new Set()
) {
  const { associatedDatagrids } = properties || {};

  if (!associatedDatagrids || typeof associatedDatagrids !== "string") {
    return { hasAssociation: false, associatedDg: [] };
  }

  const dgLinkKeyMap = getDgLinkKeys(getDgState, associatedDatagrids);
  const result = { hasAssociation: false, associatedDg: [] };

  associatedDatagrids.split(",").forEach((dgKey) => {
    const dgResult = processSingleDatagrid(
      dgKey.trim(),
      record,
      getValues,
      getDgState,
      dgLinkKeyMap,
      processedDatagrids
    );

    mergeChainedAssociations(result, dgResult);
  });

  return result;
}

export const getRecordsToRemove = (associatedDg) =>
  associatedDg
    .map((dgEntry) => ({
      dgKey: dgEntry.dgKey,
      records: dgEntry.records.filter((record) => !record.hasMultiAssociation),
    }))
    .filter((dgEntry) => dgEntry.records.length > 0);

export const getRecordsToUpdate = (associatedDg) =>
  associatedDg
    .map((dgEntry) => ({
      dgKey: dgEntry.dgKey,
      records: dgEntry.records.filter((record) => record.hasMultiAssociation),
    }))
    .filter((dgEntry) => dgEntry.records.length > 0);

function getUniqueRecordStatus(config, key, dgData, recId) {
  const status = { isValid: true, errorMsg: "" };
  if (dgData && dgData.length > 0 && config.uniqueIdentifier) {
    const filtered = dgData.filter(
      (record) =>
        record.id &&
        record.id !== recId &&
        config.uniqueIdentifier.some((id) => record[id] && record[id] === key)
    );
    if (filtered && filtered.length) {
      status.isValid = false;
      status.errorMsg = `There is already an existing record with ${key} in it.`;
    }
  }
  return status;
}

export const checkForUniqueRecord = (props) => {
  const { key, properties, getDgState, record } = props;
  const { identifiers } = properties || {};
  const dgState = getDgState() || {};
  let status = { isValid: true, errorMsg: "" };
  if (!identifiers || !dgState[key] || dgState[key].length <= 1) return status;
  const config = { uniqueIdentifier: identifiers.split(",") };
  const identifier = getDgIdentifier(config, record);
  status = getUniqueRecordStatus(config, identifier, dgState[key], record?.id);
  return status;
};

export const updateAssociatedDgFields = (
  formProperties,
  associatedDgs,
  getDgState,
  record
) => {
  const relationalDgKey = getRelationalDgKey();
  const dgLink = record[relationalDgKey];
  const dgLinkKeyMap = getDgLinkKeys(getDgState, associatedDgs);
  const { dgConfigs } = getDgState() || {};

  associatedDgs.split(",").forEach((dgKey) => {
    if (formProperties[dgKey] && formProperties[dgKey].length) {
      const config = dgConfigs[dgKey]?.custom
        ? getParsedValue(dgConfigs[dgKey]?.custom, "updateAssociatedDgFields")
        : {};
      formProperties[dgKey].forEach((assocRec) => {
        if (dgLink && dgLink === assocRec[dgLinkKeyMap[dgKey]]) {
          const { identifierLabel: label, uniqueIdentifier } = config;
          if (label && uniqueIdentifier) {
            // eslint-disable-next-line no-param-reassign
            assocRec[associatedIdLabel] = getDgOptionLabel(label, record);
            // eslint-disable-next-line no-param-reassign
            assocRec[associatedIdentifier] = getDgIdentifier(config, record);
          }
        }
      });
    }
  });
};

export const removeAssocRecFromDgPayload = (
  props,
  formData,
  processedDatagrids = new Set()
) => {
  const { properties, getDgState, record = {} } = props;
  const { associatedDatagrids } = properties || {};
  if (!associatedDatagrids) return formData;
  const relationalDgKey = getRelationalDgKey();
  const sourceRecValue = record[relationalDgKey];
  const dgLinkKeyMap = getDgLinkKeys(getDgState, associatedDatagrids);
  const formProperties = formData || {};
  const { dgConfigs } = getDgState() || {};

  associatedDatagrids.split(",").forEach((dgKey) => {
    // Prevent circular references
    if (processedDatagrids.has(dgKey)) return;
    processedDatagrids.add(dgKey);

    const associatedDg = formProperties[dgKey];
    const associatedDgProps = dgConfigs[dgKey]?.properties;
    const removedRecordIdentifiers = [];
    if (associatedDg && associatedDg.length) {
      formProperties[dgKey] = associatedDg.reduce((acc, assocRec) => {
        const assocRecValue = assocRec[dgLinkKeyMap[dgKey]];
        // multiselect case
        if (Array.isArray(assocRecValue)) {
          const updatedValue = assocRecValue.filter(
            (value) => value !== sourceRecValue
          );
          if (updatedValue.length) {
            const keyPrefix = associatedDgProps?.dynamicFieldPrefix;
            acc.push({
              ...assocRec,
              [dgLinkKeyMap[dgKey]]: updatedValue,
              ...(keyPrefix && {
                [`${keyPrefix}_${sourceRecValue}`]: undefined,
              }),
            });
          } else removedRecordIdentifiers.push(assocRec[relationalDgKey]);
        } else if (assocRecValue) {
          if (assocRecValue !== sourceRecValue) acc.push(assocRec);
          else removedRecordIdentifiers.push(assocRec[relationalDgKey]);
        }
        return acc;
      }, []);
    }
    // Recursive processing for other associated datagrids
    if (associatedDgProps?.associatedDatagrids) {
      if (removedRecordIdentifiers.filter(Boolean).length) {
        removedRecordIdentifiers.forEach((relationalKey) => {
          const assocRec = { [relationalDgKey]: relationalKey };
          removeAssocRecFromDgPayload(
            {
              record: assocRec,
              getDgState,
              properties: associatedDgProps,
            },
            formProperties,
            processedDatagrids
          );
        });
      }
    }
  });
  return formProperties;
};

export const getDgPayload = (props) => {
  const {
    key,
    properties,
    record,
    recordIndex,
    getValues,
    type,
    getDgState,
    shouldRemoveAssocRecord,
  } = props;

  const formProps = getValues();
  switch (type) {
    case "submit": {
      if (isSourceDg(properties)) {
        if (shouldRemoveAssocRecord) {
          removeAssocRecFromDgPayload(props, formProps);
        } else {
          setDgLinkKey(properties, record);
          if (
            getAssociatedDgInfo(properties, getValues, getDgState, record)
              ?.hasAssociation
          ) {
            const { associatedDatagrids: assocDgs } = properties;
            updateAssociatedDgFields(formProps, assocDgs, getDgState, record);
          }
        }
      }
      let dgList = getValues(key);
      if (Array.isArray(dgList))
        dgList = dgList.map((item, i) =>
          i === recordIndex ? { ...record, status: "saved" } : item
        );
      return {
        recordData: record,
        formProperties: { ...formProps, [key]: dgList },
      };
    }
    case "remove": {
      let filteredRecords = getValues(key);
      if (Array.isArray(filteredRecords))
        filteredRecords = filteredRecords.filter(
          (item, i) => i !== recordIndex
        );
      return { ...formProps, [key]: filteredRecords };
    }
    default:
      return null;
  }
};

export const relationalDgCheck = (props, record, getValues, getDgState) =>
  isSourceDg(props) &&
  getAssociatedDgInfo(props, getValues, getDgState, record)?.hasAssociation;

export const updateMainFormDgList = (
  props,
  setValue,
  formProps,
  getDgState
) => {
  const { dgConfigs = {} } = getDgState() || {};
  props.associatedDatagrids.split(",").forEach((dg) => {
    let val = formProps[dg];
    const { initEmpty, properties } = dgConfigs[dg] || {};
    if (!initEmpty && val?.length === 0) val = [{}];
    setValue(dg, []);
    setValue(dg, val);
    if (properties?.associatedDatagrids)
      updateMainFormDgList(properties, setValue, formProps, getDgState);
  });
};

export const getSourceRecordName = (sourceDg, config, identifier) =>
  sourceDg.reduce((acc, rec) => {
    const uniqueIdentifier = getDgIdentifier(config, rec);
    if (uniqueIdentifier === identifier) {
      return getDgOptionLabel(config.identifierLabel, rec);
    }
    return acc;
  }, "");

export const isAssocDgControllingFieldInvalid = (custom, record) => {
  const { conditional } =
    getParsedValue(custom, "isAssocDgControllingFieldValid") || {};
  if (!conditional) return false;
  const { when, eq } = conditional;
  return !isFalsy(when) && !isFalsy(eq) && !eq.includes(record[when]);
};

export const hasInvalidControllingField = (props, custom) => {
  const { record, properties, getValues, getDgState } = props;
  const isInvalid = isAssocDgControllingFieldInvalid(custom, record);
  return (
    isInvalid &&
    getAssociatedDgInfo(properties, getValues, getDgState, record)
      ?.hasAssociation
  );
};
